import { nanoid } from 'nanoid'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BackHandler, Platform, View, ViewStyle } from 'react-native'
import { Modalize, ModalizeProps } from 'react-native-modalize'

import { isMobileBrowser, isWeb } from '@common/config/env'
import usePrevious from '@common/hooks/usePrevious'
import useTheme from '@common/hooks/useTheme'
import { HEADER_HEIGHT } from '@common/modules/header/components/Header/styles'
import spacings from '@common/styles/spacings'
import common from '@common/styles/utils/common'
import { Portal } from '@gorhom/portal'
import useIsScrollable from '@web/hooks/useIsScrollable'
import { getUiType } from '@web/utils/uiType'

import Backdrop from './Backdrop'
import getStyles from './styles'

interface Props {
  id?: string
  sheetRef: React.RefObject<Modalize | null>
  closeBottomSheet?: (dest?: 'alwaysOpen' | 'default' | undefined) => void
  onBackdropPress?: () => void
  onClosed?: () => void
  onOpen?: () => void
  children?: React.ReactNode
  // Preferences
  type?: 'modal' | 'bottom-sheet'
  adjustToContentHeight?: boolean
  style?: ViewStyle
  containerInnerWrapperStyles?: ViewStyle
  flatListProps?: ModalizeProps['flatListProps']
  scrollViewProps?: ModalizeProps['scrollViewProps']
  backgroundColor?: 'primaryBackground' | 'secondaryBackground' | 'octonaryBackground'
  autoWidth?: boolean
  autoOpen?: boolean
  shouldBeClosableOnDrag?: boolean
  customZIndex?: number
  isScrollEnabled?: boolean
  withBackdropBlur?: boolean
}

const ANIMATION_DURATION: number = 250

const { isPopup } = getUiType()

const BottomSheet: React.FC<Props> = ({
  id: _id,
  type: _type,
  sheetRef,
  children,
  closeBottomSheet = () => {},
  adjustToContentHeight = true,
  style = {},
  containerInnerWrapperStyles = {},
  onClosed,
  onOpen,
  onBackdropPress,
  flatListProps,
  scrollViewProps,
  backgroundColor = 'secondaryBackground',
  autoWidth = false,
  autoOpen = false,
  shouldBeClosableOnDrag = true,
  withBackdropBlur,
  customZIndex,
  isScrollEnabled = true
}) => {
  const type = _type || (isPopup ? 'bottom-sheet' : 'modal')
  const isModal = type === 'modal'
  const { styles, theme } = useTheme(getStyles)
  const [isOpen, setIsOpen] = useState(false)
  const prevIsOpen = usePrevious(isOpen)
  const [isBackdropVisible, setIsBackdropVisible] = useState(false)
  const { isScrollable, checkIsScrollable, scrollViewRef } = useIsScrollable()

  // On mobile-web the on-screen keyboard overlays the layout viewport (the
  // viewport meta is intentionally `interactive-widget=resizes-visual`), so a
  // bottom-anchored sheet stays put and the keyboard covers whatever input sits
  // near its bottom (e.g. the Add Token address field). Modalize's own keyboard
  // avoidance relies on RN `Keyboard` events that never fire on RN-web, so we
  // lift the sheet ourselves, using the `visualViewport` API.
  //
  // TWO things matter here:
  //  1. The lift is a `transform: translateY` (see rootStyle), NOT a layout
  //     change (margin/height). A layout change reflows the page, which fires
  //     further `visualViewport` resize/scroll events; those retrigger the
  //     global keyboard `resync()` in public/viewport.js (scrollTo + synthetic
  //     resize), and on some iPhones that churn makes iOS abort and dismiss the
  //     keyboard the instant it opens. A transform is compositor-only: it moves
  //     the sheet without reflow and without emitting viewport events.
  //  2. We lift by the sheet's *actual overlap* with the keyboard (measured from
  //     the content's bottom edge), NOT by the full keyboard height. These
  //     sheets are not glued to the screen bottom, so lifting by the whole
  //     keyboard height overshoots. On tall content / short screens the sheet
  //     may not fit above the keyboard at all; in that case we lift only until
  //     its TOP reaches the top safe margin (just under the status bar / notch)
  //     — the maximum useful lift — instead of shrinking or scrolling the sheet
  //     or cropping its top off-screen.
  const contentWrapperRef = useRef<any>(null)
  const keyboardInsetRef = useRef(0)
  const [keyboardInset, setKeyboardInset] = useState(0)
  keyboardInsetRef.current = keyboardInset
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : undefined
    if (!isMobileBrowser || !isOpen || !vv) return

    // Actual top inset (status bar / notch / Dynamic Island), measured once via
    // `env(safe-area-inset-top)`. The sheet's top is never lifted above this.
    let safeTop = 0
    try {
      const probe = document.createElement('div')
      probe.style.cssText =
        'position:fixed;top:0;left:0;width:0;height:env(safe-area-inset-top);visibility:hidden;pointer-events:none;'
      document.body.appendChild(probe)
      safeTop = probe.getBoundingClientRect().height || 0
      document.body.removeChild(probe)
    } catch {
      safeTop = 0
    }
    const topMargin = Math.max(safeTop, modalTopOffset)

    const updateInset = () => {
      // Height of the layout viewport hidden by the keyboard. Sub-threshold
      // values are address-bar noise.
      const occluded = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      if (occluded <= 80) {
        setKeyboardInset(0)
        return
      }
      const node = contentWrapperRef.current as HTMLElement | null
      if (!node || typeof node.getBoundingClientRect !== 'function') {
        // No node to measure — fall back to full keyboard height.
        setKeyboardInset(occluded)
        return
      }
      // `rect` already includes the currently-applied lift, so add it back to
      // recover the sheet's natural (un-lifted) edges.
      const rect = node.getBoundingClientRect()
      const currentLift = keyboardInsetRef.current
      const naturalBottom = rect.bottom + currentLift
      const naturalTop = rect.top + currentLift
      const keyboardTop = window.innerHeight - occluded

      // Enough to clear the keyboard (with a small breathing gap)...
      const overlapLift = naturalBottom - keyboardTop + 12
      // ...but never lift the sheet's top above the top safe margin. On short
      // screens where the sheet can't fully clear the keyboard, this pins the
      // top just under the status bar (maximum lift) rather than cropping it.
      const maxLift = naturalTop - topMargin

      setKeyboardInset(Math.max(0, Math.min(overlapLift, maxLift)))
    }

    updateInset()
    vv.addEventListener('resize', updateInset)
    vv.addEventListener('scroll', updateInset)

    return () => {
      vv.removeEventListener('resize', updateInset)
      vv.removeEventListener('scroll', updateInset)
      setKeyboardInset(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Ensures ID is unique per component to avoid duplicates when multiple bottom sheets are rendered
  const id = useMemo(() => `${_id || 'bottom-sheet'}-${nanoid(6)}`, [_id])

  const autoOpened: React.MutableRefObject<boolean> = useRef(false)
  const setRef = useCallback(
    (node: HTMLElement | null) => {
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      sheetRef.current = node
      // check if component is mounted and if should autoOpen
      if (autoOpen && sheetRef.current && !autoOpened.current) {
        sheetRef.current.open()
        autoOpened.current = !autoOpened.current // ensure that the bottom sheet auto-opens only once
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoOpen]
  )

  useEffect(() => {
    if (prevIsOpen && !isOpen) {
      setTimeout(() => {
        // Delays the backdrop unmounting because of the closing animation duration
        setIsBackdropVisible(false)
      }, ANIMATION_DURATION)
    }
  }, [isOpen, prevIsOpen])

  // Hook up the back button (or action) to close the bottom sheet
  useEffect(() => {
    if (!isOpen) return

    const backAction = () => {
      if (isOpen) {
        closeBottomSheet()
        // Returning true prevents execution of the default native back handling
        return true
      }

      return false
    }

    if (Platform.OS === 'web') return
    const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction)

    return () => backHandler.remove()
  }, [closeBottomSheet, isOpen])

  const modalTopOffset = useMemo(() => {
    if (isPopup && isModal) return 0
    if (isWeb) return HEADER_HEIGHT - 20

    return HEADER_HEIGHT + 10
  }, [isModal])

  return (
    <Portal hostName="global">
      {/* Wrapping the content in a View with a stable `key` prevents Portal */}
      {/* from losing track of its subtree during React reconciliation and re-renders. */}
      {/* Without this, the backdrop stays, but Modalize could disappear */}
      {/* without even triggering `onClose` or (this) component unmount */}
      <View
        key={`portal-host-${id}`}
        style={[styles.portalHost, customZIndex ? { zIndex: customZIndex } : {}]}
      >
        {!!isBackdropVisible && (
          <Backdrop
            isVisible={isBackdropVisible}
            isBottomSheetVisible={isOpen}
            customZIndex={customZIndex ? customZIndex - 1 : undefined}
            onPress={() => {
              closeBottomSheet()
              !!onBackdropPress && onBackdropPress()
            }}
            withBlur={withBackdropBlur}
          />
        )}
        <Modalize
          // The key is used to force the re-render of the component when there is an opened
          // bottom sheet and the user navigates to a route that also has a bottom sheet. Without
          // this key or without a unique key, the bottom sheet will not close when navigating
          key={id}
          ref={setRef}
          contentRef={scrollViewRef as any}
          modalStyle={[
            styles.bottomSheet,
            isModal
              ? { ...styles.modal, ...(autoWidth ? { maxWidth: 'unset', width: 'auto' } : {}) } as any
              : {},
            { backgroundColor: theme[backgroundColor] },
            style
          ]}
          rootStyle={[
            isPopup && isModal ? spacings.phSm : {},
            // Compositor-only lift above the on-screen keyboard on mobile-web.
            // Applied to Modalize's full-screen container so the whole sheet
            // rides up without a reflow (see the keyboardInset effect above).
            keyboardInset ? { transform: [{ translateY: -keyboardInset }] } : {}
          ]}
          handleStyle={[
            styles.dragger,
            isModal
              ? {
                  display: 'none'
                }
              : {}
          ]}
          handlePosition="inside"
          useNativeDriver={!isWeb}
          avoidKeyboardLikeIOS
          modalTopOffset={modalTopOffset}
          threshold={90}
          adjustToContentHeight={adjustToContentHeight}
          disableScrollIfPossible={false}
          withOverlay={false}
          onBackButtonPress={() => true}
          panGestureEnabled={shouldBeClosableOnDrag}
          {...(!flatListProps
            ? {
                scrollViewProps: {
                  bounces: false,
                  keyboardShouldPersistTaps: 'handled',
                  ...(!isScrollEnabled && {
                    scrollEnabled: false,
                    nestedScrollEnabled: true,
                    contentContainerStyle: { flex: 1 }
                  }),
                  ...(scrollViewProps || {})
                }
              }
            : {})}
          {...(flatListProps
            ? {
                flatListProps: {
                  bounces: false,
                  keyboardShouldPersistTaps: 'handled',
                  ...(flatListProps || {})
                }
              }
            : {})}
          openAnimationConfig={{
            timing: { duration: ANIMATION_DURATION, delay: 0 }
          }}
          closeAnimationConfig={{
            timing: { duration: ANIMATION_DURATION, delay: 0 }
          }}
          onLayout={checkIsScrollable}
          onOpen={() => {
            setIsOpen(true)
            setIsBackdropVisible(true)
            !!onOpen && onOpen()
          }}
          onClose={() => setIsOpen(false)}
          onClosed={() => !!onClosed && onClosed()}
        >
          {!flatListProps && (
            <View
              ref={contentWrapperRef}
              testID={isOpen ? 'bottom-sheet' : undefined}
              style={[
                isScrollEnabled && isScrollable ? spacings.prTy : {},
                common.fullWidth,
                containerInnerWrapperStyles
              ]}
            >
              {children}
            </View>
          )}
        </Modalize>
      </View>
    </Portal>
  )
}

export default React.memo(BottomSheet)
